// React imports
import { useEffect, useMemo } from "react";

// ERP Common imports
import formBuilderDeConversion from "@/utils/form-builder-deconversion";

// Module pathname imports
import { PathnameAccounting } from "../../../constants/pathnames/pathname.accounting";
import { PathnameCrm } from "../../../constants/pathnames/pathname.crm";
import { PathnameInventory } from "../../../constants/pathnames/pathname.inventory";
import { PathnameManufacturing } from "../../../constants/pathnames/pathname.manufacturing";
import { PathnamePurchase } from "../../../constants/pathnames/pathname.procurement";
import { PathnameRental } from "../../../constants/pathnames/pathname.rental";
import { PathnameUsers } from "../../../constants/pathnames/pathname.user";
import { PathnameHrms } from "../../../constants/pathnames/pathname.hrms";

// Types for better type safety
export interface TableColumn {
  header: string;
  accessorKey: string;
  visible: boolean;
}

export interface FormMember {
  field_type: string;
  table?: string;
  table_columns?: TableColumn[];
  [key: string]: any;
}

export interface FormSection {
  field_type: string;
  label: string;
  is_accordion: boolean;
  id: string;
  remove_section: boolean;
  form_switcher_label?: string;
  form_switcher_name?: string;
  is_section_field?: boolean;
  section_type?: string;
  switcher_forms?: any;
  members: FormMember[];
}

export interface TabData {
  tab_order: number;
  data: FormSection[];
}

export interface ProcessedData {
  tab?: TabData[];
  section?: FormSection[];
  tabsData?: TabData[];
}

// Constants for module paths
const MODULE_PATHS = {
  accounting: PathnameAccounting.FORMS,
  inventory: PathnameInventory.FORMS,
  manufacturing: PathnameManufacturing.FORMS,
  procurement: PathnamePurchase.FORMS,
  crm: PathnameCrm.FORMS,
  rental: PathnameRental.FORMS,
  user: PathnameUsers.FORMS,
  hrms: PathnameHrms.FORMS,
} as const;

export type ModuleType = keyof typeof MODULE_PATHS;

// Helper function to format text (converts snake_case to Title Case)
export const formatText = (text: string): string => {
  return text ? text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ') : '';
};

// Helper function to get redirection path
const getRedirectionPath = (currentModule: string): string => {
  if (!isValidModule(currentModule)) {
    throw new Error(`Invalid module: ${currentModule}`);
  }
  return MODULE_PATHS[currentModule as ModuleType];
};

// Type guard for module validation
const isValidModule = (module: string): module is ModuleType => {
  return module in MODULE_PATHS;
};

// Helper function to create default table columns
export const createDefaultTableColumns = (): TableColumn[] => [
  { header: "Column 1", accessorKey: "column_1", visible: true },
  { header: "Column 2", accessorKey: "column_2", visible: true },
];

// Helper function to process table columns
export const processTableColumns = (
  member: FormMember,
  formBuilder: any
): TableColumn[] => {
  // Return existing columns if they exist
  if (member.table_columns?.length) {
    return member.table_columns;
  }

  // Check if table exists and has fields
  if (!member.table || !formBuilder.fields?.[member.table]) {
    return createDefaultTableColumns();
  }

  const fields = formBuilder.fields[member.table];
  if (!Array.isArray(fields) || fields.length === 0) {
    return createDefaultTableColumns();
  }

  return fields.map((field: any, index: number) => ({
    header: formatText(field.field),
    accessorKey: field.field,
    visible: index <= 3, // Show first 4 columns by default
  }));
};

// Helper function to process members
export const processMembers = (
  sections: FormSection[],
  formBuilder: any
): FormMember[] => {
  return sections.flatMap((section) =>
    section.members.map((member) => {
      if (member.field_type === "table") {
        return {
          ...member,
          table_columns: processTableColumns(member, formBuilder),
        };
      }
      return { ...member };
    })
  );
};

// Helper function to extract sections data
export const extractSections = (sections: FormSection[]): Omit<FormSection, 'members'>[] => {
  return sections.map(({
    field_type,
    label,
    is_accordion,
    id,
    remove_section,
    form_switcher_label,
    form_switcher_name,
    is_section_field,
    section_type,
    switcher_forms,
  }) => ({
    field_type,
    label,
    is_accordion,
    id,
    remove_section,
    form_switcher_label,
    form_switcher_name,
    is_section_field,
    section_type,
    switcher_forms,
  }));
};

// Main processing function
const processData = (
  data: ProcessedData,
  activeTabId: number = 0,
  formBuilder: any,
  tabsData: TabData[] | null,
  // Callback functions
  callbacks: {
    removeSection: () => void;
    remove: () => void;
    setTabsData: (data: TabData[]) => void;
    append: (members: FormMember[]) => void;
    appendSection: (sections: Omit<FormSection, 'members'>[]) => void;
  }
): void => {
  const { removeSection, remove, setTabsData, append, appendSection } = callbacks;

  try {
    // Clear existing data
    removeSection();
    remove();

    if (data.tab) {
      processTabData(data.tab, activeTabId, formBuilder, callbacks);
    } else if (data.section) {
      processSectionData(data.section, formBuilder, callbacks);
    } else if (data.tabsData && tabsData) {
      processExistingTabsData(tabsData, activeTabId, callbacks);
    } else {
      throw new Error("Invalid data format: Missing required properties (tab, section, or tabsData)");
    }
  } catch (error) {
    console.error("Error processing data:", error);
    // You might want to show a user-friendly error message instead of alert
    alert(error instanceof Error ? error.message : "An error occurred while processing data");
  }
};

// Process tab data
const processTabData = (
  tabs: TabData[],
  activeTabId: number,
  formBuilder: any,
  callbacks: {
    setTabsData: (data: TabData[]) => void;
    append: (members: FormMember[]) => void;
    appendSection: (sections: Omit<FormSection, 'members'>[]) => void;
  }
): void => {
  const { setTabsData, append, appendSection } = callbacks;

  setTabsData(tabs);

  const activeTab = tabs.find(tab => tab.tab_order === activeTabId + 1);
  if (!activeTab?.data) {
    console.warn(`No active tab found for tab order: ${activeTabId + 1}`);
    return;
  }

  const sections = extractSections(activeTab.data);
  const members = processMembers(activeTab.data, formBuilder);

  append(members);
  appendSection(sections);
};

// Process section data
const processSectionData = (
  sections: FormSection[],
  formBuilder: any,
  callbacks: {
    append: (members: FormMember[]) => void;
    appendSection: (sections: Omit<FormSection, 'members'>[]) => void;
  }
): void => {
  const { append, appendSection } = callbacks;

  const extractedSections = extractSections(sections);
  const members = processMembers(sections, formBuilder);

  append(members);
  appendSection(extractedSections);
};

// Process existing tabs data
const processExistingTabsData = (
  tabsData: TabData[],
  activeTabId: number,
  callbacks: {
    append: (members: FormMember[]) => void;
    appendSection: (sections: Omit<FormSection, 'members'>[]) => void;
  }
): void => {
  const { append, appendSection } = callbacks;

  const activeTab = tabsData.find(tab => tab.tab_order === activeTabId + 1);
  if (!activeTab?.data) {
    console.warn(`No active tab found for tab order: ${activeTabId + 1}`);
    return;
  }

  const sections = extractSections(activeTab.data);
  const members = activeTab.data.flatMap(section =>
    section.members.map(member => ({ ...member }))
  );

  if (members.length > 0) append(members);
  if (sections.length > 0) appendSection(sections);
};

// Custom hook for form data processing
export const useFormDataProcessor = (
  formBuilder: any,
  tabsData: TabData[],
  activeTabValue: number,
  callbacks: {
    removeSection: () => void;
    remove: () => void;
    setTabsData: (data: TabData[]) => void;
    append: (members: FormMember[]) => void;
    appendSection: (sections: Omit<FormSection, 'members'>[]) => void;
  }
) => {
  const { removeSection, remove, append } = callbacks;

  // Memoize the serialized form data to avoid unnecessary re-renders
  const serializedFormData = useMemo(() =>
    JSON.stringify(formBuilder?.formData),
    [formBuilder?.formData]
  );

  // Memoize the initial data processing
  const initialData = useMemo(() => {
    if (!formBuilder?.formData?.length) return null;

    try {
      return formBuilderDeConversion(formBuilder.formData);
    } catch (error) {
      console.error('Error processing form data:', error);
      return null;
    }
  }, [serializedFormData]);

  useEffect(() => {
    // Handle case when there's no initial data
    if (!initialData) {
      removeSection();
      remove();
      append([]);
      return;
    }

    // Determine processing strategy based on available data
    try {
      if (tabsData.length > 0) {
        // Process existing tabs data
        processData(
          { tabsData: tabsData },
          activeTabValue,
          formBuilder,
          tabsData,
          callbacks
        );
      } else if (initialData.tab) {
        // Process new tab data
        processData(
          initialData,
          activeTabValue,
          formBuilder,
          tabsData,
          callbacks
        );
      } else if (initialData.section) {
        // Process section data
        processData(
          initialData,
          0, // No active tab for section-only data
          formBuilder,
          tabsData,
          callbacks
        );
      } else {
        console.warn('No valid data structure found in initialData');
        removeSection();
        remove();
        append([]);
      }
    } catch (error) {
      console.error('Error in form data processing:', error);
      // Reset to clean state on error
      removeSection();
      remove();
      append([]);
    }
  }, [activeTabValue, serializedFormData, tabsData.length, initialData, callbacks, formBuilder]);

  return { initialData };
};

// Alternative: Direct useEffect implementation (if you prefer not to use custom hook)
export const useFormDataEffect = (
  formBuilder: any,
  tabsData: TabData[],
  activeTabValue: number = 0,
  callbacks: {
    removeSection: () => void;
    remove: () => void;
    setTabsData: (data: TabData[]) => void;
    append: (members: FormMember[]) => void;
    appendSection: (sections: Omit<FormSection, 'members'>[]) => void;
  }
) => {
  const { removeSection, remove, append } = callbacks;

  useEffect(() => {
    let initialData: ProcessedData | null = null;

    // Process initial data
    if (formBuilder?.formData?.length) {
      try {
        initialData = formBuilderDeConversion(formBuilder.formData);
      } catch (error) {
        console.error('Error converting form data:', error);
      }
    }

    // Handle empty initial data
    if (!initialData) {
      removeSection();
      remove();
      append([]);
      return;
    }

    // Process data based on available structure
    try {
      if (tabsData.length > 0) {
        processData(
          { tabsData: tabsData },
          activeTabValue,
          formBuilder,
          tabsData,
          callbacks
        );
      } else if (initialData.tab) {
        processData(
          initialData,
          activeTabValue,
          formBuilder,
          tabsData,
          callbacks
        );
      } else if (initialData.section) {
        processData(
          initialData,
          0,
          formBuilder,
          tabsData,
          callbacks
        );
      } else {
        console.warn('Invalid data structure in initialData');
        removeSection();
        remove();
        append([]);
      }
    } catch (error) {
      console.error('Error processing form data:', error);
      // Reset to clean state on error
      removeSection();
      remove();
      append([]);
    }
  }, [activeTabValue, JSON.stringify(formBuilder?.formData), tabsData.length]);
};

// Usage example:
/*
// Option 1: Using custom hook
const { initialData } = useFormDataProcessor(
  formBuilder,
  tabsData,
  activeTabValue,
  {
    removeSection,
    remove,
    setTabsData,
    append,
    appendSection
  }
);

// Option 2: Using direct useEffect
useFormDataEffect(
  formBuilder,
  tabsData,
  activeTabValue,
  {
    removeSection,
    remove,
    setTabsData,
    append,
    appendSection
  }
);
*/

// Path generation utility (separate from the effect)
export const generateModulePath = (currentModule: string): string => {
  try {
    const redirectionPath = getRedirectionPath(currentModule);
    return `/dashboard/${currentModule}${redirectionPath}`;
  } catch (error) {
    console.error('Error generating module path:', error);
    throw error;
  }
};

export const generateCustomFormPath = (currentModule,redirectionPath): any => {
  try {
    return `/dashboard/${currentModule}${redirectionPath}`;
  } catch (error) {
    console.error('Error generating module path:', error);
    throw error;
  }
};